import React from "react";
import {
  CANONICAL_STAGES,
  STAGE_DETAILS,
  STAGE_LABELS,
  metricContextLabel,
  outreachCopy,
  projectContextLabel,
  stageIndex,
  type CanonicalStage,
  type QueueWorkflowItem,
} from "./gcRoiIntroductionWorkflow";

interface Props {
  item: QueueWorkflowItem;
  onClose: () => void;
}

function timelineLabel(stage: CanonicalStage, currentIdx: number, idx: number): string {
  if (idx < currentIdx) return "Completed stage";
  if (idx === currentIdx) return "Current status";
  return "Pending";
}

export default function GcRoiIntroductionWorkflowTracker({ item, onClose }: Props) {
  const stage = (item.workflow_stage || "ADDED_TO_QUEUE") as CanonicalStage;
  const currentIdx = stageIndex(stage);
  const stageLabel = item.stage_label || STAGE_LABELS[stage] || stage;
  const progressPct =
    typeof item.progress_pct === "number"
      ? item.progress_pct
      : Math.round(((currentIdx + 1) / CANONICAL_STAGES.length) * 100);

  return (
    <div
      className="gc-roi-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="gc-roi-tracker-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-roi-tracker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gc-roi-modal-header">
          <div>
            <div id="gc-roi-tracker-title" className="gc-roi-modal-title">
              Full introduction workflow
            </div>
            <div className="gc-roi-modal-subtitle">
              GC-controlled stages only. Stage markers are visual and do not change status.
            </div>
          </div>
          <button type="button" className="gc-roi-modal-close" onClick={onClose} aria-label="Close">
            {"\u00D7"}
          </button>
        </div>

        <div className="gc-roi-tracker-summary">
          <div>
            <div className="gc-roi-meta-value">{item.company_name}</div>
            <div className="gc-roi-meta-sub">
              {metricContextLabel(item.metric_key)} {"\u00B7"} {projectContextLabel(item)}
            </div>
          </div>
          <div className="gc-roi-status-pill">{stageLabel}</div>
        </div>

        <div className="gc-roi-governance">
          {item.governance_message ||
            "Queue placement and stage advances are GC-controlled. No outreach, underwriting, or partner engagement is implied before Introduction Sent."}
        </div>

        <div className="gc-roi-progress-track" aria-hidden="true">
          <div className="gc-roi-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="gc-roi-meta-sub" style={{ marginBottom: 16 }}>
          Progress {currentIdx + 1} of {CANONICAL_STAGES.length}
        </div>

        <ol className="gc-roi-stage-row" aria-label="Introduction workflow stages">
          {CANONICAL_STAGES.map((s, idx) => {
            const state =
              idx < currentIdx ? "completed" : idx === currentIdx ? "current" : "pending";
            return (
              <li key={s} className={`gc-roi-stage-item gc-roi-stage-${state}`}>
                {idx > 0 && (
                  <span
                    className={`gc-roi-stage-connector ${
                      idx <= currentIdx ? "gc-roi-connector-done" : ""
                    }`}
                    aria-hidden="true"
                  />
                )}
                <span className="gc-roi-stage-circle" aria-hidden="true">
                  {idx < currentIdx ? "\u2713" : idx + 1}
                </span>
                <span className="gc-roi-stage-label">{STAGE_LABELS[s]}</span>
              </li>
            );
          })}
        </ol>

        <div className="gc-roi-timeline">
          <div className="gc-roi-meta-label">Introduction timeline</div>
          {CANONICAL_STAGES.map((s, idx) => {
            if (idx > currentIdx) return null;
            return (
              <div key={s} className="gc-roi-timeline-card">
                <div className="gc-roi-timeline-title">{STAGE_LABELS[s]}</div>
                <div className="gc-roi-meta-sub">{timelineLabel(s, currentIdx, idx)}</div>
                <div className="gc-roi-timeline-detail">{STAGE_DETAILS[s]}</div>
              </div>
            );
          })}
        </div>

        <div className="gc-roi-tracker-footer">
          <div>
            <div className="gc-roi-meta-label">Current owner</div>
            <div className="gc-roi-owner-pill">{item.owner || "Unassigned"}</div>
          </div>
          <div>
            <div className="gc-roi-meta-label">Next required action</div>
            <div className="gc-roi-meta-value">{item.next_action || "No further action"}</div>
          </div>
          <div>
            <div className="gc-roi-meta-label">GC control / outreach</div>
            <div className="gc-roi-meta-value">{outreachCopy(Boolean(item.outreach_occurred))}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
